// DEM-08 (multi-provider v5): seleção de modelo filtrado por provider.
import { useQuery } from '@tanstack/react-query';
import { api, type LlmModel, type Modality } from '@/lib/api';
import { Select } from '@/components/ui/Select';
import { fmtMoneyUSD } from '@/lib/format';

interface ModelSelectProps {
  /** Filtra modelos por provider key (anthropic, openai). */
  provider?: string;
  /** Filtra por modality (default: 'text' pra chat). */
  modality?: Modality;
  /** Filtra por status (default: active + available_via_oauth). */
  statusFilter?: 'active' | 'available_via_oauth' | 'all';
  value: string;
  onChange: (modelKey: string, model?: LlmModel) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Opção explícita para remover uma escolha persistida (ex.: fallback). */
  emptyOptionLabel?: string;
}

export function ModelSelect({
  provider,
  modality = 'text',
  statusFilter = 'all',
  value,
  onChange,
  label = 'Modelo',
  placeholder = 'Escolha o modelo…',
  disabled,
  emptyOptionLabel,
}: ModelSelectProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['llm-models', provider, modality],
    queryFn: () => api.listLlmModels({ provider, modality, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!provider,
  });

  const models = (data?.models ?? []).filter((m) => {
    if (statusFilter === 'all') return m.status === 'active' || m.status === 'available_via_oauth';
    return m.status === statusFilter;
  });

  const options = [
    ...(emptyOptionLabel ? [{ value: '', label: emptyOptionLabel }] : []),
    ...models.map((m) => ({
      value: m.model_key,
      label: m.display_name,
      description: [
        m.tier,
        m.input_cost_per_m != null ? `${fmtMoneyUSD(m.input_cost_per_m)}/M in` : null,
        m.status === 'available_via_oauth' ? '⚠ via OAuth only' : null,
      ].filter(Boolean).join(' · '),
    })),
  ];

  return (
    <Select
      label={label}
      placeholder={isLoading ? 'Carregando modelos…' : placeholder}
      options={options}
      value={value}
      onChange={(v) => {
        const m = models.find((x) => x.model_key === v);
        onChange(v, m);
      }}
      disabled={disabled || !provider || isLoading}
    />
  );
}
